import React from 'react';
import UrlUtils from '@spothero/utils/url';
import CityAPI from 'api/city';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import AirportAPI from 'api/airport';
import {loadable} from 'router/routes';
import {createCleanedData, fetchCleanedData} from 'router/router-utils';
import {Page} from 'utils/page-utils';
import getInitialStateCityContext from 'pages/search-transient-event-monthly/redux/get-initial-state-city-context';
import getInitialStateDestinationContext from 'pages/search-transient-event-monthly/redux/get-initial-state-destination-context';
import DestinationAPI from 'api/destination';
import SearchAirportPage from 'pages/search-airport';
/*
    The Search route corresponds to different page components based on a few factors:
    - whether this is a transient, monthly, event, or airport search
    - whether this is desktop or mobile experience
    - whether the location context of a search is address, city, destination, or event

    This route could be simplified by doing the following:
    - Merging mobile and desktop features

    This could also be simplified by dropping product features from the search experience,
    which is less likely to occur. It is likely that as SpotHero grows, these
    experiences will become increasingly differentiated.

    The primary reason for housing all of these different experiences under the
    same /search URL + query parameters is that it makes the marketing dept's strategy
    and work simpler: Marketing and Partners only need to determine the associated
    query parameters to append to the base URL.

    https://spothero.atlassian.net/wiki/spaces/CONS/pages/1186529350/Marketing+Search+URL+Strategy
    https://spothero.atlassian.net/wiki/spaces/CONS/pages/1351582635/Search+URLS+-+What+to+Keep+What+to+Deprecate
*/

const SearchMobileTransient = loadable(() =>
    import('pages/search-transient-event-monthly/SearchMobileTransient')
);
const SearchMobileMonthly = loadable(() =>
    import('pages/search-transient-event-monthly/SearchMobileMonthly')
);
const SearchMobileEvent = loadable(() =>
    import('pages/search-transient-event-monthly/SearchMobileEvent')
);
const SearchMobileDestination = loadable(() =>
    import('pages/search-transient-event-monthly/SearchMobileDestination')
);
const SearchDesktopTransient = loadable(() =>
    import('pages/search-transient-event-monthly/SearchDesktopTransient')
);
const SearchDesktopMonthly = loadable(() =>
    import('pages/search-transient-event-monthly/SearchDesktopMonthly')
);
const SearchDesktopEvent = loadable(() =>
    import('pages/search-transient-event-monthly/SearchDesktopEvent')
);
const SearchDesktopDestination = loadable(() =>
    import('pages/search-transient-event-monthly/SearchDesktopDestination')
);

const Search = ({configData, isMonthly, destination, pageType}) => {
    const {isMobile} = configData;

    if (isMonthly) {
        return isMobile ? <SearchMobileMonthly /> : <SearchDesktopMonthly />;
    }

    if (destination?.airport) {
        return <SearchAirportPage />;
    }

    if (isMobile) {
        return pageType === 'event' ? (
            <SearchMobileEvent />
        ) : !isEmpty(destination) ? (
            <SearchMobileDestination />
        ) : (
            <SearchMobileTransient />
        );
    }

    return pageType === 'event' ? (
        <SearchDesktopEvent />
    ) : !isEmpty(destination) ? (
        <SearchDesktopDestination />
    ) : (
        <SearchDesktopTransient />
    );
};

async function getCitySlug(cityId, searchParams) {
    if (!isEmpty(searchParams)) {
        const {
            page_info: {
                setup: {
                    city: {slug},
                },
            },
        } = searchParams;

        return slug;
    }

    const {slug} = await CityAPI.getById(cityId);

    return slug;
}

const AirportSearchInitialState = ({state, citySlug, destination, query}) => {
    const {
        relative_url: slug,
        airport: {iata_code: iataCode},
    } = destination;

    return SearchAirportPage.getInitialState({
        state: {
            ...state,
            destination: {data: destination},
        },
        params: {city: citySlug, slug, iata: iataCode},
        query,
    });
};

async function searchCity({id, state, searchParams, query, cookies}) {
    const city = await getCitySlug(id, searchParams);

    return getInitialStateCityContext({
        state,
        params: {city},
        query,
        cookies,
    });
}

async function searchDestination({id, state, searchParams, query, cookies}) {
    const destination = await DestinationAPI.get(id).then(res => res.data.data);
    const {relative_url: slug, spothero_city: cityId, airport} = destination;
    const citySlug = await getCitySlug(cityId, searchParams);

    if (airport) {
        return AirportSearchInitialState({
            state,
            citySlug,
            destination,
            query,
        });
    } else {
        return getInitialStateDestinationContext({
            state: {
                ...state,
                destination: {data: destination},
            },
            params: {city: citySlug, slug},
            query,
            cookies,
        });
    }
}

async function searchAirport({id, state, searchParams, query, cookies}) {
    const {
        data: {data: destination},
    } = await AirportAPI.getByCode(id);
    const {spothero_city: cityId} = destination;
    const citySlug = await getCitySlug(cityId, searchParams);

    return AirportSearchInitialState({
        state,
        citySlug,
        destination,
        query,
    });
}

Search.getInitialState = ({state, query, route, cookies}) => {
    const {
        location: {state: {searchParams = {}} = {}},
    } = route;
    const {id, kind} = query;
    const methodParams = {
        state,
        searchParams,
        query,
        cookies,
    };

    if (!isNil(kind)) {
        // new /search experience with kind and id provided in query string to determine how search should behave
        if (kind === 'address' && !isEmpty(searchParams)) {
            // address searches from the UI do not have an ID so we use the cleaned params returned by the search-params
            // endpoint, which has already happened in the search controls, to execute the proper search and
            // `searchParams` is passed through the location 'state' in order to route using search metadata
            // that means there is no need to call the search-params endpoint since they're already available
            // and happens after initial page load when the user performs a search through the UI
            const {city, search: searchState} = state;

            // This overrides incorrect starts/ends coming back from the search-params response
            // This is only necessary due to way Search.getInitialState handles address search pages
            // TODO: Remove once BE issue is resolved https://spothero.atlassian.net/browse/CONWEB-291
            searchParams.starts = query.starts || searchParams.starts;
            searchParams.ends = query.ends || searchParams.ends;

            // eslint-disable-next-line camelcase
            searchParams.search_string =
                query.search_string || searchParams.search_string;

            return createCleanedData({
                currentCity: city,
                cleanedParams: searchParams,
                currentSearchState: searchState,
            });

            // if searchParams is empty, that means this search was initialized on page load and not through the UI
            // just allow it to fall through to the bottom of this method where we will fetch new cleaned data based on
            // the url that the user has provided in the address bar
        } else if (!isNil(id)) {
            methodParams.id = id;

            switch (kind) {
                case Page.CITY:
                    return searchCity(methodParams);

                case Page.DESTINATION:
                    return searchDestination(methodParams);

                case Page.AIRPORT:
                    return searchAirport(methodParams);

                case Page.EVENT:
                    query.eid = id;

                    // translate /search?kind=event&id=1 URL to /search?eid=1 for calling fetchCleanedData since it only responds with
                    // that format for event kind searches, otherwise it would error without altering the BE to accept this type of URL
                    return fetchCleanedData({
                        state,
                        route: {
                            location: {
                                pathname: '/search',
                                search: `?${UrlUtils.createQueryString(query)}`,
                            },
                        },
                    });
            }
        }
    }

    // regular address search on initial page load, run a new search based on the url
    return fetchCleanedData({state, route});
};

Search.propTypes = {
    configData: PropTypes.object,
    isMonthly: PropTypes.bool,
    destination: PropTypes.object,
    pageType: PropTypes.string,
};

export default Search;
